import { DispatchDate } from '@utils/dispatch';
import { framedByDefault } from '@utils/frames';
import {
  isStoryblokRichTextPresent,
  renderStoryblokRichText,
} from '@utils/renderStoryblokRichText';
import { useTranslation } from 'next-i18next';

import { purchasingState } from '../../../helpers/purchasing';
import {
  MultiassetStoryblok,
  ProductV2Storyblok,
  RichtextStoryblok,
} from '../../../types/generated-storyblok';
import { ProductDispatchDate } from '../../ProductDispatchDate/ProductDispatchDate';
import { ProductEditionSize } from './ProductEditionSize';

type ProductReleaseInfoProps = {
  // purchasing controller
  unlocked?: boolean;
  available?: boolean;
  tags?: string[];
  freeShipping?: boolean;
  framingOffered?: string | null;
  dispatchDate: DispatchDate;
  //
  // product storyblok
  releaseType: StoryblokReleaseType;
  closingDate?: string;
  releaseNote?: StoryblokTextAreaType | RichtextStoryblok;
  editionNote?: string;
  editionSize?: string | number;
  releaseCollectionGallery?: MultiassetStoryblok;
  medium?: StoryblokDatatypeMedium[] | Array<string | number>;
  editionFormat?: ProductV2Storyblok['editionFormat'];
  //
  onEditionSizeAnchorClick?: () => void;
};

const editionFormatMapper: Record<
  Exclude<ProductV2Storyblok['editionFormat'], ''>,
  string
> = {
  'limited': 'productDetails.editionFormat.limited',
  'timed': 'productDetails.editionFormat.timed',
  'open': 'productDetails.editionFormat.open',
  'unique': 'productDetails.editionFormat.unique',
  'series': 'productDetails.editionFormat.series',
};

export const ProductReleaseInfo = (props: ProductReleaseInfoProps) => {
  const { t } = useTranslation(['product']);

  const state = purchasingState({
    releaseType: props.releaseType,
    closingDate: props.closingDate,
    unlocked: props.unlocked,
    available: props?.available,
    tags: props?.tags,
  });

  const isSoldOut = state === 'SOLD_OUT';
  const isFreeShipping = props?.freeShipping;
  const isHandFinished = props?.medium?.includes('hand');
  const isFrameIncluded = framedByDefault(props?.framingOffered);
  const { editionFormat } = props;

  return (
    <div className="flex w-full flex-col gap-y-3 text-center text-neutral-4">
      {editionFormat && editionFormatMapper[editionFormat] ? (
        <p>{t(editionFormatMapper[editionFormat])}</p>
      ) : null}

      <ProductEditionSize
        editionNote={props.editionNote}
        editionSize={props.editionSize}
        hasReleaseCollectionGallery={!!props?.releaseCollectionGallery?.length}
        onAnchorButtonClick={props.onEditionSizeAnchorClick}
      />

      {isHandFinished && <p>{t('variants.handFinishedYes')}</p>}

      {isFrameIncluded && <p>{t('framing.included.title')}</p>}

      {isStoryblokRichTextPresent(props.releaseNote) && (
        <div>
          {renderStoryblokRichText(props.releaseNote, {
            paragraphClassName: 'pb-0',
          })}
        </div>
      )}

      {!isSoldOut && isFreeShipping && <p>{t('benefits.shipping.free')}</p>}

      {!isSoldOut ? (
        <ProductDispatchDate
          dispatchDate={props.dispatchDate}
          variant="estimation"
        />
      ) : null}
    </div>
  );
};
